/**
 * Small drawing toolkit for the cartoon look: soft vertical gradients, darker
 * rim outlines, glossy highlights and smooth (never jagged) silhouettes.
 *
 * Colour helpers are memoised: moving layers call them every frame and the
 * hex parsing in core is not free.
 */
import { cssRgba, mixHexColors, shadeHexColor } from '@dropstack/core';
import { rimWidth, type SceneSize } from '../layout.ts';

export type Ctx = CanvasRenderingContext2D;
export interface Point {
  x: number;
  y: number;
}

const MEMO_LIMIT = 4096;
const memo = new Map<string, string>();
function remember(key: string, make: () => string): string {
  const hit = memo.get(key);
  if (hit !== undefined) return hit;
  if (memo.size >= MEMO_LIMIT) memo.clear();
  const v = make();
  memo.set(key, v);
  return v;
}

/** Darken (amount < 0) or lighten (amount > 0) a hex colour, keeping its hue. */
export function shade(color: string, amount: number): string {
  return remember(`s${color}|${amount}`, () => shadeHexColor(color, amount));
}

/** Blend two hex colours (t = 0 → a, 1 → b). */
export function mix(a: string, b: string, t: number): string {
  return remember(`m${a}|${b}|${t}`, () => mixHexColors(a, b, t));
}

/** Canvas colour string with extra transparency. */
export function rgba(color: string, alpha: number): string {
  return remember(`a${color}|${alpha}`, () => cssRgba(color, alpha));
}

/** Default rim colour for a fill: a richer, darker version of it. */
export function rimOf(color: string): string {
  return shade(color, -0.32);
}

/** Pick an item cyclically (safe for any integer, including negatives). */
export function pick<T>(items: readonly T[], i: number): T {
  if (items.length === 0) throw new Error('pick: empty list');
  return items[((Math.floor(i) % items.length) + items.length) % items.length]!;
}

/** Stroke the current path as a cartoon rim. `scale` multiplies the standard width. */
export function strokeRim(ctx: Ctx, color: string, size: SceneSize, scale = 1): void {
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.strokeStyle = color;
  ctx.lineWidth = rimWidth(size) * scale;
  ctx.stroke();
}

/** Vertical gradient from y0 to y1 with evenly spaced (or explicit) stops. */
export function vGradient(ctx: Ctx, y0: number, y1: number, stops: readonly (string | readonly [number, string])[]): CanvasGradient {
  const g = ctx.createLinearGradient(0, y0, 0, y1 === y0 ? y0 + 1 : y1);
  stops.forEach((s, i) => {
    if (typeof s === 'string') g.addColorStop(stops.length === 1 ? 0 : i / (stops.length - 1), s);
    else g.addColorStop(Math.min(1, Math.max(0, s[0])), s[1]);
  });
  return g;
}

/** Cartoon body fill: slightly lighter at the top, slightly deeper at the bottom. */
export function bodyGradient(ctx: Ctx, color: string, y0: number, y1: number, light = 0.14, dark = -0.1): CanvasGradient {
  return vGradient(ctx, y0, y1, [shade(color, light), color, shade(color, dark)]);
}

export function circlePath(ctx: Ctx, x: number, y: number, r: number): void {
  ctx.moveTo(x + r, y);
  ctx.arc(x, y, Math.max(0, r), 0, Math.PI * 2);
}

export function ellipsePath(ctx: Ctx, x: number, y: number, rx: number, ry: number, rotation = 0): void {
  const a = Math.max(0, rx);
  const b = Math.max(0, ry);
  ctx.moveTo(x + a * Math.cos(rotation), y + a * Math.sin(rotation));
  ctx.ellipse(x, y, a, b, rotation, 0, Math.PI * 2);
}

/** Rounded rectangle path (radius clamped to fit). */
export function roundRectPath(ctx: Ctx, x: number, y: number, w: number, h: number, r: number): void {
  const rr = Math.max(0, Math.min(r, Math.abs(w) / 2, Math.abs(h) / 2));
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.arcTo(x + w, y, x + w, y + rr, rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.arcTo(x + w, y + h, x + w - rr, y + h, rr);
  ctx.lineTo(x + rr, y + h);
  ctx.arcTo(x, y + h, x, y + h - rr, rr);
  ctx.lineTo(x, y + rr);
  ctx.arcTo(x, y, x + rr, y, rr);
  ctx.closePath();
}

/**
 * Smooth curve through a polyline: quadratic segments between midpoints
 * (the classic "Chaikin-ish" trick). Starts with moveTo unless `connect`.
 */
export function smoothLine(ctx: Ctx, pts: readonly Point[], connect = false): void {
  if (pts.length === 0) return;
  const first = pts[0]!;
  if (connect) ctx.lineTo(first.x, first.y);
  else ctx.moveTo(first.x, first.y);
  if (pts.length < 3) {
    for (const p of pts.slice(1)) ctx.lineTo(p.x, p.y);
    return;
  }
  for (let i = 1; i < pts.length - 1; i++) {
    const p = pts[i]!;
    const n = pts[i + 1]!;
    ctx.quadraticCurveTo(p.x, p.y, (p.x + n.x) / 2, (p.y + n.y) / 2);
  }
  const last = pts[pts.length - 1]!;
  ctx.lineTo(last.x, last.y);
}

export interface Circle {
  x: number;
  y: number;
  r: number;
}

/**
 * A cartoon blob made of overlapping circles with a single outer rim: stroke
 * every circle with a double-width rim, then fill them all on top so only the
 * outside edge of the union keeps its outline.
 */
export function circleBlob(ctx: Ctx, circles: readonly Circle[], fill: string | CanvasGradient, rim: string, size: SceneSize, rimScale = 1): void {
  ctx.beginPath();
  for (const c of circles) circlePath(ctx, c.x, c.y, c.r);
  strokeRim(ctx, rim, size, rimScale * 2);
  ctx.fillStyle = fill;
  ctx.fill();
}

/** Soft glossy highlight: a pale, flattened ellipse near the top-left of a shape. */
export function gloss(ctx: Ctx, x: number, y: number, rx: number, ry: number, alpha = 0.45, rotation = -0.5): void {
  ctx.beginPath();
  ellipsePath(ctx, x, y, rx, ry, rotation);
  ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
  ctx.fill();
}

/** Radial glow that fades to transparent. */
export function glow(ctx: Ctx, x: number, y: number, r: number, color: string, alpha = 1): void {
  if (r <= 0) return;
  const g = ctx.createRadialGradient(x, y, 0, x, y, r);
  g.addColorStop(0, rgba(color, alpha));
  g.addColorStop(0.45, rgba(color, alpha * 0.45));
  g.addColorStop(1, rgba(color, 0));
  ctx.fillStyle = g;
  ctx.beginPath();
  circlePath(ctx, x, y, r);
  ctx.fill();
}

/** Four-point twinkle star (sparkles, glints). */
export function twinkle(ctx: Ctx, x: number, y: number, r: number, color: string, alpha: number): void {
  if (alpha <= 0.01 || r <= 0) return;
  const w = r * 0.28;
  ctx.beginPath();
  ctx.moveTo(x, y - r);
  ctx.quadraticCurveTo(x + w * 0.35, y - w * 0.35, x + r, y);
  ctx.quadraticCurveTo(x + w * 0.35, y + w * 0.35, x, y + r);
  ctx.quadraticCurveTo(x - w * 0.35, y + w * 0.35, x - r, y);
  ctx.quadraticCurveTo(x - w * 0.35, y - w * 0.35, x, y - r);
  ctx.closePath();
  ctx.fillStyle = rgba(color, alpha);
  ctx.fill();
}

/** A leaf shape (pointed ellipse) centred on (x, y), pointing along `angle`. */
export function leafPath(ctx: Ctx, x: number, y: number, length: number, width: number, angle: number): void {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  const tipX = x + c * length * 0.5;
  const tipY = y + s * length * 0.5;
  const tailX = x - c * length * 0.5;
  const tailY = y - s * length * 0.5;
  const nx = -s * width;
  const ny = c * width;
  ctx.moveTo(tailX, tailY);
  ctx.quadraticCurveTo(x + nx, y + ny, tipX, tipY);
  ctx.quadraticCurveTo(x - nx, y - ny, tailX, tailY);
  ctx.closePath();
}

export interface Blob {
  x: number;
  y: number;
  rx: number;
  ry: number;
}

/** Like circleBlob, for ellipses (flat acacia canopies, cloud banks, bushes). */
export function ellipseBlob(ctx: Ctx, blobs: readonly Blob[], fill: string | CanvasGradient, rim: string, size: SceneSize, rimScale = 1): void {
  ctx.beginPath();
  for (const b of blobs) ellipsePath(ctx, b.x, b.y, b.rx, b.ry);
  strokeRim(ctx, rim, size, rimScale * 2);
  ctx.fillStyle = fill;
  ctx.fill();
}

/** Point on a quadratic Bézier curve at t ∈ [0, 1]. */
export function quadPoint(a: Point, c: Point, b: Point, t: number): Point {
  const u = 1 - t;
  return { x: u * u * a.x + 2 * u * t * c.x + t * t * b.x, y: u * u * a.y + 2 * u * t * c.y + t * t * b.y };
}

/**
 * Closed outline of a tapering ribbon around a centre line (trunks, stalks,
 * vines, streams). `widthAt(t)` is the full width at t ∈ [0, 1] along the
 * line. The ends are rounded off by the smoothing.
 */
export function ribbonPath(ctx: Ctx, center: readonly Point[], widthAt: (t: number) => number): void {
  const n = center.length;
  if (n < 2) return;
  const left: Point[] = [];
  const right: Point[] = [];
  for (let i = 0; i < n; i++) {
    const p = center[i]!;
    const a = center[Math.max(0, i - 1)]!;
    const b = center[Math.min(n - 1, i + 1)]!;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1;
    const half = widthAt(i / (n - 1)) / 2;
    left.push({ x: p.x - (dy / len) * half, y: p.y + (dx / len) * half });
    right.push({ x: p.x + (dy / len) * half, y: p.y - (dx / len) * half });
  }
  smoothLine(ctx, left);
  smoothLine(ctx, right.reverse(), true);
  ctx.closePath();
}
